import React, { useState, lazy, Suspense, useEffect } from 'react';
import { useCosts } from '../context/CostsContext';
import { useLanguage } from '../i18n';
import inputsTranslations from './inputs/i18n';
import './InputAccordion.css';

// Only PurchaseAndFinancing is open by default, load it synchronously
import PurchaseAndFinancing from './inputs/PurchaseAndFinancing';

// Lazy load closed sections - they load when user opens them
const StartupCosts = lazy(() => import('./inputs/StartupCosts'));
const FixedRecurringCosts = lazy(() => import('./inputs/FixedRecurringCosts'));
const VariableRecurringCosts = lazy(() => import('./inputs/VariableRecurringCosts'));
const ScheduledCapexGrid = lazy(() => import('./inputs/ScheduledCapexGrid'));
const EmergencyFund = lazy(() => import('./inputs/EmergencyFund'));
const DepreciationSettings = lazy(() => import('./inputs/DepreciationSettings'));
const WerbungskostenSettings = lazy(() => import('./inputs/WerbungskostenSettings'));

const AccordionSection = ({ title, summary, isOpen, onToggle, children }) => {
  return (
    <div className={`accordion-section ${isOpen ? 'open' : ''}`}>
      <button className="accordion-header" onClick={onToggle}>
        <div className="accordion-title">
          <span className="accordion-icon">{isOpen ? '\u25BC' : '\u25B6'}</span>
          <h3>{title}</h3>
        </div>
        {!isOpen && summary && <div className="accordion-summary">{summary}</div>}
      </button>
      {isOpen && (
        <div className="accordion-content">
          {children}
        </div>
      )}
    </div>
  );
};

function InputAccordion() {
  const { costs } = useCosts();
  const { t, registerTranslations } = useLanguage();

  // Register inputs translations when component loads
  useEffect(() => {
    registerTranslations(inputsTranslations);
  }, [registerTranslations]);

  const [openSections, setOpenSections] = useState({
    purchase: true,
    startup: false,
    fixedRecurring: false,
    variableRecurring: false,
    capex: false,
    emergency: false,
    depreciation: false,
    werbungskosten: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate summaries
  const purchaseSummary = () => {
    const totalCapital = costs.purchase.equityCapital + costs.purchase.debtCapital;
    const leverage = totalCapital > 0
      ? ((costs.purchase.debtCapital / totalCapital) * 100).toFixed(0)
      : 0;
    return `\u20AC${costs.property.purchasePrice.toLocaleString('de-DE')} | \u20AC${costs.purchase.equityCapital.toLocaleString('de-DE')} EK | \u20AC${costs.purchase.debtCapital.toLocaleString('de-DE')} FK | ${leverage}% LTV`;
  };

  const startupSummary = () => {
    const total = costs.startup.grunderwerbsteuer + costs.startup.notarkosten +
                  costs.startup.grundbuchkosten + costs.startup.maklerkosten +
                  costs.startup.renovationBudget;
    return total > 0 ? `Total: \u20AC${total.toLocaleString('de-DE')}` : 'Auto-calculated from Bundesland';
  };

  const fixedRecurringSummary = () => {
    const total = costs.fixedRecurring.grundsteuer + costs.fixedRecurring.gebaeudeversicherung +
                  (costs.fixedRecurring.custom?.reduce((sum, item) => sum + item.amount, 0) || 0);
    return `Annual: \u20AC${total.toLocaleString('de-DE')}`;
  };

  const variableRecurringSummary = () => {
    const lower = costs.variableRecurring.maintenancePercent.lower;
    const upper = costs.variableRecurring.maintenancePercent.upper;
    return `Maintenance: ${lower}-${upper}% | Mgmt: ${costs.fixedRecurring.propertyManagementPercent}%`;
  };

  const capexSummary = () => {
    const horizonYears = costs.projection?.horizonYears || 15;
    const years = Array.from({ length: horizonYears }, (_, i) => i + 1);
    const total = years.reduce((sum, year) => {
      const items = costs.scheduledCapex[year] || [];
      return sum + items.reduce((yearSum, item) => yearSum + (item.cost || 0), 0);
    }, 0);
    return `${horizonYears}-year total: \u20AC${total.toLocaleString('de-DE')}`;
  };

  const emergencySummary = () => {
    const buildingValue = costs.property.purchasePrice * (1 - costs.property.landSharePercent / 100);
    const annualReserve = (buildingValue * costs.emergencyFund.annualReservePercent) / 100;
    return `${costs.emergencyFund.annualReservePercent}% annual reserve (~\u20AC${Math.round(annualReserve).toLocaleString('de-DE')}/year)`;
  };

  const depreciationSummary = () => {
    const method = costs.depreciation.afaMethod;
    const methodLabel = method === 'degressive' ? 'Degressive 5%' :
                        method === 'denkmalschutz' ? 'Denkmalschutz' :
                        'Standard (AfA)';
    return `${methodLabel} | Renovation: ${costs.depreciation.renovationTreatment}`;
  };

  const werbungskostenSummary = () => {
    const wk = costs.werbungskosten;
    const annual = wk.steuerberaterAnnual + wk.sonstigePauschale;
    const parts = [];
    if (wk.disagioPercent > 0) parts.push(`Disagio: ${wk.disagioPercent}%`);
    parts.push(`\u20AC${annual.toLocaleString('de-DE')}/yr`);
    if (wk.mietausfallversicherungPercent > 0) parts.push(`Versicherung: ${wk.mietausfallversicherungPercent}%`);
    return parts.join(' | ');
  };

  return (
    <div className="input-accordion">
      <AccordionSection
        title={t('accordion.purchaseFinancing')}
        summary={purchaseSummary()}
        isOpen={openSections.purchase}
        onToggle={() => toggleSection('purchase')}
      >
        <PurchaseAndFinancing />
      </AccordionSection>

      <AccordionSection
        title={t('accordion.startupCosts')}
        summary={startupSummary()}
        isOpen={openSections.startup}
        onToggle={() => toggleSection('startup')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <StartupCosts />
        </Suspense>
      </AccordionSection>

      <AccordionSection
        title={t('accordion.fixedRecurring')}
        summary={fixedRecurringSummary()}
        isOpen={openSections.fixedRecurring}
        onToggle={() => toggleSection('fixedRecurring')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <FixedRecurringCosts />
        </Suspense>
      </AccordionSection>

      <AccordionSection
        title={t('accordion.variableRecurring')}
        summary={variableRecurringSummary()}
        isOpen={openSections.variableRecurring}
        onToggle={() => toggleSection('variableRecurring')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <VariableRecurringCosts />
        </Suspense>
      </AccordionSection>

      <AccordionSection
        title={t('accordion.scheduledCapex')}
        summary={capexSummary()}
        isOpen={openSections.capex}
        onToggle={() => toggleSection('capex')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <ScheduledCapexGrid />
        </Suspense>
      </AccordionSection>

      <AccordionSection
        title={t('accordion.emergencyReserve')}
        summary={emergencySummary()}
        isOpen={openSections.emergency}
        onToggle={() => toggleSection('emergency')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <EmergencyFund />
        </Suspense>
      </AccordionSection>

      <AccordionSection
        title={t('accordion.depreciation')}
        summary={depreciationSummary()}
        isOpen={openSections.depreciation}
        onToggle={() => toggleSection('depreciation')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <DepreciationSettings />
        </Suspense>
      </AccordionSection>

      <AccordionSection
        title={t('accordion.werbungskosten')}
        summary={werbungskostenSummary()}
        isOpen={openSections.werbungskosten}
        onToggle={() => toggleSection('werbungskosten')}
      >
        <Suspense fallback={<div className="loading-section">Loading...</div>}>
          <WerbungskostenSettings />
        </Suspense>
      </AccordionSection>
    </div>
  );
}

export default InputAccordion;
